import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true });

// Global 401 handler — if the session expires or is missing, bounce the
// user to the login screen. Auth endpoints themselves opt out so the
// login/callback screens can surface their own errors.
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const onLogin = typeof window !== "undefined" && (
      window.location.pathname === "/login" ||
      window.location.hash?.includes("session_id=")
    );
    if (status === 401 && !url.startsWith("/auth/") && !onLogin) {
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  },
);

export const endpoints = {
  // ---- The Mirror ----
  mirrorUsage: () => api.get("/mirror/usage"),
  mirrorCreate: (body) => api.post("/mirror/session", body),
  mirrorGet: (sid) => api.get(`/mirror/session/${sid}`),
  mirrorSend: (sid, text) => api.post(`/mirror/session/${sid}/message`, { text }),
  mirrorClose: (sid) => api.post(`/mirror/session/${sid}/close`),
  mirrorDelete: (sid) => api.delete(`/mirror/session/${sid}`),

  // ---- Nervous System (Soul → Regulation & Presence) ----
  nsSaveEntry: (body) => api.post("/nervous-system/entry", body),
  nsSignals: () => api.get("/nervous-system/signals"),
  nsSaveInsight: (text) => api.put("/nervous-system/insight", { text }),

  // ---- Auth ----
  authMe: () => api.get("/auth/me"),
  authLogout: () => api.post("/auth/logout"),
  adminShadowMode: (on) => api.post("/auth/admin/shadow-mode", { on }),
  adminResetDemoData: () => api.post("/auth/admin/reset-demo-data"),
  adminMigrateLegacy: () => api.post("/auth/admin/migrate-legacy"),

  // ---- Subscription / entitlements ----
  subscriptionMe: () => api.get("/subscription/me"),
  subscriptionPricing: () => api.get("/subscription/pricing"),
  subscriptionUpgrade: (plan = "monthly", origin_url) =>
    api.post("/subscription/upgrade", { plan, origin_url: origin_url || window.location.origin }),
  subscriptionStatus: (sessionId) => api.get(`/subscription/status/${sessionId}`),
  subscriptionCancel: () => api.post("/subscription/cancel"),
  adminPreviewTier: (tier) => api.post("/subscription/admin/preview-tier", { tier }),

  // ---- Complimentary Pro (comp_pro router) ----
  redeemProCode: (code) => api.post("/subscription/redeem", { code }),
  myGrant: () => api.get("/subscription/my-grant"),
  adminListCodes: () => api.get("/subscription/admin/codes"),
  adminCreateCode: (body) => api.post("/subscription/admin/codes", body),
  adminDisableCode: (id) => api.post(`/subscription/admin/codes/${id}/disable`),
  adminListGrants: (onlyActive = true) =>
    api.get(`/subscription/admin/grants?only_active=${onlyActive}`),
  adminDirectGrant: (body) => api.post("/subscription/admin/grant", body),
  adminRevokeGrant: (id) => api.post(`/subscription/admin/grants/${id}/revoke`),

  // ---- Monthly Becoming Reflection (Pro) ----
  monthlyBecomingReflection: () => api.get("/becoming/monthly-reflection"),

  // ---- Pro Compass Insights ("Your Compass Is Evolving") ----
  compassEvolvingInsights: () => api.get("/soul-compass/evolving-insights"),

  home: () => api.get("/home/summary"),
  profile: () => api.get("/profile"),
  updateProfile: (data) => api.put("/profile", data),
  checkUsername: (u) => api.get(`/profile/username/check`, { params: { u } }),

  rituals: () => api.get("/rituals"),
  createRitual: (data) => api.post("/rituals", data),
  updateRitual: (id, data) => api.put(`/rituals/${id}`, data),
  deleteRitual: (id) => api.delete(`/rituals/${id}`),
  completeRitual: (id) => api.post(`/rituals/${id}/complete`),
  completionsToday: () => api.get("/rituals/completions/today"),
  streak: () => api.get("/rituals/streak"),

  tracking: (days = 14) => api.get(`/tracking?days=${days}`),
  trackingToday: () => api.get("/tracking/today"),  updateTracking: (data) => api.put("/tracking", data),
  trackingSummary: (days = 7) => api.get(`/tracking/summary?days=${days}`),

  smartEntrySchema: () => api.get("/smart-entry/schema"),
  smartEntryParse: (body) => api.post("/smart-entry/parse", body),
  smartEntrySave: (body) => api.post("/smart-entry/save", body),
  smartEntryHistory: (hours = 24) => api.get(`/smart-entry/history?hours=${hours}`),
  smartEntryGaps: (entryDate) => api.get(`/smart-entry/gaps${entryDate ? `?entry_date=${entryDate}` : ""}`),

  journal: (params) => api.get("/journal", { params: params || {} }),
  createJournal: (data) => api.post("/journal", data),
  updateJournal: (id, data) => api.patch(`/journal/${id}`, data),
  deleteJournal: (id) => api.delete(`/journal/${id}`),
  journalStreak: () => api.get("/journal/streak"),

  // Soul Work — inner-transformation flow (kept separate from Soul Compass).
  soulWorkState: () => api.get("/soul-work/state"),
  soulWorkStart: (data) => api.post("/soul-work/session/start", data || {}),
  soulWorkGet: (id) => api.get(`/soul-work/session/${id}`),
  soulWorkAnswer: (id, body) => api.post(`/soul-work/session/${id}/answer`, body),
  soulWorkAnalyze: (id) => api.post(`/soul-work/session/${id}/analyze`),
  soulWorkConfirm: (id, body) => api.post(`/soul-work/session/${id}/confirm`, body),
  soulWorkUnderstand: (id, body) => api.put(`/soul-work/session/${id}/understand`, body),
  soulWorkReframePropose: (id) => api.post(`/soul-work/session/${id}/reframe/propose`),
  soulWorkReframeUpdate: (id, body) => api.put(`/soul-work/session/${id}/reframe`, body),
  soulWorkSaveToJournal: (id) => api.post(`/soul-work/session/${id}/save-to-journal`),
  soulWorkAbandon: (id) => api.delete(`/soul-work/session/${id}`),
  soulWorkPatterns: () => api.get("/soul-work/patterns"),
  soulWorkDaily: () => api.get("/soul-work/daily"),
  soulCompassPatternNudge: () => api.get("/soul-compass/pattern-nudge"),
  soulCompassNudgeDismiss: (body) => api.post("/soul-compass/pattern-nudge/dismiss", body),

  // Pro Ritual Engine
  proDailyPlan: () => api.get("/pro/daily-plan"),
  proToggleItem: (planId, itemId, completed) =>
    api.post(`/pro/daily-plan/${planId}/items/${itemId}/toggle`, completed === undefined ? {} : { completed }),
  proConsistency: () => api.get("/pro/consistency"),
  proReflection: () => api.post("/pro/reflection"),
  milestoneCategories: () => api.get("/pro/milestones/categories"),
  milestones: () => api.get("/pro/milestones"),
  upsertMilestone: (category, data) => api.put(`/pro/milestones/${category}`, data),
  addMilestoneProgress: (category, entry) => api.post(`/pro/milestones/${category}/progress`, entry),
  shareMilestone: (category, data) => api.post(`/pro/milestones/${category}/share`, data || {}),
  deleteMilestone: (category) => api.delete(`/pro/milestones/${category}`),

  proSetPro: (isPro) => api.post("/profile/pro", { is_pro: isPro }),
  weeklyRecap: () => api.get("/pro/weekly-recap"),

  // Files & media library
  libraryUpload: (formData) => api.post("/library/upload", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  library: (kind) => api.get("/library", { params: kind ? { kind } : {} }),
  libraryStats: () => api.get("/library/stats"),
  deleteLibraryItem: (id) => api.delete(`/library/${id}`),
  aiPrompt: (mood) => api.post("/ai/journal-prompt", { mood }),
  aiInsight: (content) => api.post("/ai/journal-insight", { content }),
  aiRitual: () => api.post("/ai/ritual-suggestion", {}),
  affirmationToday: () => api.get("/affirmation/today"),
  futureSelfWhisper: () => api.get("/future-self/whisper"),
  nightlyVisualization: () => api.get("/nightly-visualization/today"),
  becoming: () => api.get("/community/becoming"),
  updateBecomingNote: (postId, note) => api.patch(`/community/posts/${postId}`, { becoming_note: note }),

  // Unified tracking log reminders
  listTrackingReminders: (params) => api.get("/tracking-reminders", { params }),
  createTrackingReminder: (data) => api.post("/tracking-reminders", data),
  updateTrackingReminder: (id, data) => api.patch(`/tracking-reminders/${id}`, data),
  deleteTrackingReminder: (id) => api.delete(`/tracking-reminders/${id}`),

  meta: () => api.get("/meta"),
  wins: (ageGroup, goals) => {
    const p = new URLSearchParams();
    if (ageGroup && ageGroup !== "all") p.set("age_group", ageGroup);
    if (goals && goals.length) p.set("goals", goals.join(","));
    return api.get(`/community/wins?${p.toString()}`);
  },
  checkin: (data) => api.post("/community/checkin", data),
  uploadPhoto: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  },
  fileUrl: (path) => `${API}/files/${path}`,
  follows: () => api.get("/follows"),
  toggleFollow: (author) => api.post(`/follows/${encodeURIComponent(author)}`),

  // ---- Ask the Collective: pre-post review + Shared Experiences ----
  // (Shared Experiences = one-level responses beneath a question. NOT comments.)
  collectiveReviewQuestion: (text) => api.post("/collective/questions/review", { text }),
  sharedExperiencesList: (postId) => api.get(`/collective/questions/${postId}/shared-experiences`),
  sharedExperienceCreate: (postId, content) => api.post(`/collective/questions/${postId}/shared-experiences`, { content }),
  sharedExperienceDelete: (seId) => api.delete(`/collective/shared-experiences/${seId}`),
  sharedExperienceHelpful: (seId) => api.post(`/collective/shared-experiences/${seId}/helpful`),
  sharedExperienceReport: (seId, category, note) => api.post(`/collective/shared-experiences/${seId}/report`, { category, note }),
  moderationSharedExperiences: (status = "pending") => api.get("/moderation/shared-experiences", { params: { status } }),
  moderationSharedExperienceResolve: (seId, action, note) => api.post(`/moderation/shared-experiences/${seId}/resolve`, { action, note }),

  spaCreate: (data) => api.post("/wellness/rituals", data),
  spaList: (params = {}) => api.get("/wellness/rituals", { params }),
  spaByCode: (code) => api.get(`/wellness/rituals/${encodeURIComponent(code)}`),
  spaAdopt: (code) => api.post(`/wellness/rituals/${encodeURIComponent(code)}/adopt`),
  spaReact: (code, reaction) => api.post(`/wellness/rituals/${encodeURIComponent(code)}/react`, { reaction }),
  spaQuestions: (code) => api.get(`/wellness/rituals/${encodeURIComponent(code)}/questions`),
  spaAskQuestion: (code, content) => api.post(`/wellness/rituals/${encodeURIComponent(code)}/questions`, { content }),
  spaAnswerQuestion: (code, qid, content) => api.post(`/wellness/rituals/${encodeURIComponent(code)}/questions/${qid}/answer`, { content }),
  spaReportRitual: (code, reason, note) => api.post(`/wellness/rituals/${encodeURIComponent(code)}/report`, { reason, note }),
  adminListRitualReports: (status = "pending") => api.get("/admin/ritual-reports", { params: { status } }),
  adminResolveRitualReport: (id, action) => api.post(`/admin/ritual-reports/${id}/resolve`, { action }),
  procedures: () => api.get("/procedures"),
  proceduresMeta: () => api.get("/procedures/meta"),
  createProcedure: (data) => api.post("/procedures", data),
  updateProcedure: (id, data) => api.put(`/procedures/${id}`, data),
  updateFitnessEntry: (id, data) => api.put(`/fitness-entries/${id}`, data),
  updatePeptideEntry: (id, data) => api.put(`/peptide-entries/${id}`, data),
  deleteProcedure: (id) => api.delete(`/procedures/${id}`),
  // ---- New categorized trackers ----
  fitnessEntries: (limit) => api.get(`/fitness-entries${limit ? `?limit=${limit}` : ""}`),
  createFitnessEntry: (data) => api.post("/fitness-entries", data),
  deleteFitnessEntry: (id) => api.delete(`/fitness-entries/${id}`),
  fitnessWeeklySummary: (weeks) => api.get(`/fitness/weekly-summary${weeks ? `?weeks=${weeks}` : ""}`),
  fitnessRestDayCheck: () => api.get("/fitness/rest-day-check"),
  peptideEntries: (limit) => api.get(`/peptide-entries${limit ? `?limit=${limit}` : ""}`),
  createPeptideEntry: (data) => api.post("/peptide-entries", data),
  deletePeptideEntry: (id) => api.delete(`/peptide-entries/${id}`),
  peptideCycles: () => api.get("/peptide-cycles"),
  createPeptideCycle: (data) => api.post("/peptide-cycles", data),
  updatePeptideCycle: (id, data) => api.put(`/peptide-cycles/${id}`, data),
  deletePeptideCycle: (id) => api.delete(`/peptide-cycles/${id}`),
  dailyRoutine: (date) => api.get(`/daily-routine/${date}`),
  upsertDailyRoutine: (data) => api.put("/daily-routine", data),
  listDailyRoutine: (limit) => api.get(`/daily-routine${limit ? `?limit=${limit}` : ""}`),
  trackingHeatmap: (days) => api.get(`/tracking/heatmap${days ? `?days=${days}` : ""}`),
  themedWeekCurrent: () => api.get("/themed-weeks/current"),
  updateReminder: (ritualId, remindAt) => api.put(`/rituals/${ritualId}/reminder`, { remind_at: remindAt }),

  sendProcedurePhotos: (formData) => api.post("/wellness/procedure-photos", formData, { headers: { "Content-Type": "multipart/form-data" } }),
  procedurePhotos: () => api.get("/procedure-photos"),
  procedurePhotosUnseen: () => api.get("/procedure-photos/unseen-count"),
  markProcedurePhotoSeen: (id) => api.post(`/procedure-photos/${id}/seen`),
  reactToProcedurePhoto: (id, reaction) => api.post(`/procedure-photos/${id}/react`, { reaction }),
  spaSentPhotos: (wellnessName) => api.get("/wellness/sent", { params: { wellness_name: wellnessName } }),

  // Wellness practice ↔ Client photo consent
  requestSpaConsent: (data) => api.post("/wellness/consent-requests", data),
  listSpaConsents: (status) => api.get("/wellness/consent-requests", { params: status ? { status } : {} }),
  listClientSpaConsents: (status) => api.get("/client/wellness-consents", { params: status ? { status } : {} }),
  acceptSpaConsent: (id) => api.post(`/client/wellness-consents/${id}/accept`),
  denySpaConsent: (id) => api.post(`/client/wellness-consents/${id}/deny`),
  revokeSpaConsent: (id) => api.post(`/client/wellness-consents/${id}/revoke`),

  circles: () => api.get("/community/circles"),
  posts: (circle, opts = {}) => {
    const params = new URLSearchParams();
    if (circle) params.set("circle", circle);
    if (opts.ageGroup && opts.ageGroup !== "all") params.set("age_group", opts.ageGroup);
    if (Array.isArray(opts.goals) && opts.goals.length > 0) params.set("goals", opts.goals.join(","));
    const qs = params.toString();
    return api.get(`/community/posts${qs ? `?${qs}` : ""}`);
  },
  createPost: (data) => api.post("/community/posts", data),
  editPost: (id, data) => api.patch(`/community/posts/${id}`, data),
  deletePost: (id) => api.delete(`/community/posts/${id}`),
  likePost: (id) => api.post(`/community/posts/${id}/like`),
  cheerPost: (id) => api.post(`/community/posts/${id}/cheer`),
  reactPost: (id, kind) => api.post(`/community/posts/${id}/react`, { kind }),
  askQuestion: (id, content) => api.post(`/community/posts/${id}/questions`, { content }),
  listQuestions: (id) => api.get(`/community/posts/${id}/questions`),
  approveAnswer: (qid, editedAnswer) =>
    api.post(`/community/questions/${qid}/approve`, editedAnswer ? { edited_answer: editedAnswer } : {}),
  answerQuestion: (qid, content) => api.post(`/community/questions/${qid}/answer`, { content }),
  notifications: (unseenOnly = false) => api.get("/notifications", { params: unseenOnly ? { unseen_only: true } : {} }),
  markNotificationSeen: (id) => api.post(`/notifications/${id}/seen`),
  markAllNotificationsSeen: () => api.post("/notifications/mark-all-seen"),
  ensureWeeklyRecapNotification: () => api.post("/notifications/ensure-weekly-recap"),

  n8GuideToday: (category, hour) => api.get("/n8-guide/today", {
    params: {
      ...(category ? { category } : {}),
      ...(hour !== undefined && hour !== null ? { hour } : {}),
    },
  }),
  n8GuideReflection: (answer) => api.post("/n8-guide/reflection", { answer }),
  n8BookmarkInsight: (payload) => api.post("/n8-guide/insights/bookmark", payload),
  dailyPlan: () => api.get("/n8-guide/daily-plan"),
  generateDailyPlan: (data) => api.post("/n8-guide/daily-plan", data),
  pageStructure: (page) => api.get(`/page-structure/${page}`),
  updatePageStructure: (page, data) => api.put(`/page-structure/${page}`, data),
  footerStructure: () => api.get("/footer-structure"),
  updateFooterStructure: (data) => api.put("/footer-structure", data),
  addComment: (id, data) => api.post(`/community/posts/${id}/comments`, data),
  moderationQueue: (status = "pending") => api.get("/moderation/queue", { params: { status } }),
  setPostAuthenticity: (id, data) => api.post(`/community/posts/${id}/authenticity`, data),
  moderationAiAssist: (id) => api.post(`/moderation/${id}/ai-assist`),
};
